import type { DataRepresentation } from "@tracksuit/frontend/schemas";

export const FUNNEL_QUESTIONS = [
  "UNPROMPTED_AWARENESS",
  "PROMPTED_AWARENESS",
  "CONSIDERATION",
  "INVESTIGATION",
  "USAGE",
  "PREFERENCE",
];

export const SAMPLE_QUALITY_INDICATORS = {
  RELIABLE: "RELIABLE",
  DIRECTIONAL: "DIRECTIONAL",
  INSUFFICIENT: "INSUFFICIENT",
} as const;

export type SampleQualityIndicator =
  (typeof SAMPLE_QUALITY_INDICATORS)[keyof typeof SAMPLE_QUALITY_INDICATORS];

/**
 * Minimal replacement for classnames and clsx
 */
export const cx = (...args: (string | boolean | undefined | null)[]) => {
  return args.filter(Boolean).join(" ");
};

/**
 * Format percentages
 */
export const percentageFormatter = Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
});

/**
 * Round numbers to textual representations
 */
export const roundNumber = (number: number, isApprox?: boolean) => {
  if (Math.abs(number) < 1000) {
    return (isApprox ? "~ " : "") + String(Math.round(number));
  }

  return (
    (isApprox ? "~ " : "") +
    (Math.abs(number) < 1000000
      ? `${parseFloat((number / 1000).toFixed(0))}K`
      : `${parseFloat((number / 1000000).toFixed(1))}M`)
  );
};

/**
 * Round positive numbers with significant figure rules
 */
export const formatCompactNumber = (number: number) => {
  if (number < 10000) {
    const rounded = Math.round(number / 1000);
    return `${rounded}K`;
  }
  if (number < 100000) {
    const rounded = Math.round(number / 1000);
    return `${rounded}K`;
  }
  if (number < 1000000) {
    const rounded = Math.round(number / 10000) * 10;
    return `${rounded}K`;
  }
  if (number < 10000000) {
    const rounded = Math.round(number / 100000) / 10;
    return `${parseFloat(rounded.toFixed(1))}M`;
  }
  if (number < 100000000) {
    const rounded = Math.round(number / 1000000);
    return `${rounded}M`;
  }
  const rounded = Math.round(number / 10000000) * 10;
  return `${rounded}M`;
};

/**
 * Formats based on data representation
 */
export const formatNumberRepresentation = (
  value: number,
  format: DataRepresentation,
) => {
  return format === "percentage"
    ? percentageFormatter.format(value)
    : roundNumber(value);
};

/**
 * Sort by question type
 */
export const sortQuestionTypes = (a: any, b: any) => {
  return (
    FUNNEL_QUESTIONS.indexOf(a.questionType) -
    FUNNEL_QUESTIONS.indexOf(b.questionType)
  );
};

/**
 * Check for external URLs
 */
export const isExternalURL = (url: string) => {
  return url.startsWith("http");
};

/**
 * Utility to add all items in an array to a Set
 */
export const addAllToSet = <T>(array: T[], set: Set<T>): Set<T> => {
  array.forEach((item) => set.add(item));
  return set;
};

/**
 * Formats a word into its possessive form
 */
export const formatPossessive = (word: string) => {
  return word.endsWith("s") ? `${word}'` : `${word}'s`;
};

export const replace = (
  text: string,
  find: RegExp | string,
  replace: React.ReactNode,
) => {
  return text
    .split(find)
    .flatMap((part) => [part, replace])
    .slice(0, -1);
};

/**
 * Transforms 1st person pronouns to 3rd person pronouns
 */
export const transformToThirdPerson = (text: string): string => {
  const pronounMap: Record<string, string> = {
    I: "they",
    i: "they",
    me: "them",
    my: "their",
    mine: "theirs",
    myself: "themselves",
    we: "they",
    us: "them",
    our: "their",
    ours: "theirs",
    ourselves: "themselves",
  };

  let result = text;
  Object.entries(pronounMap).forEach(([firstPerson, thirdPerson]) => {
    const regex = new RegExp(`\\b${firstPerson}\\b`, "gi");
    result = result.replace(regex, thirdPerson);
  });

  return result;
};
